import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquarePlus, X, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/** Floating note box: remarks on a generation, token issues, anything worth flagging. */
export const NoteWidget = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (message.trim().length < 5) return toast.error("Write a line or two so we can act on it.");
    setSending(true);
    try {
      await api.post("/notes", { message, email, page: pathname });
      toast.success("Note sent — thank you, we read every one.");
      setMessage("");
      setEmail("");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send that note. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="note-open-btn"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 border border-[#0f172a] bg-white px-5 py-3 text-sm font-medium text-[#0f172a] shadow-[4px_4px_0px_#0f172a] transition-transform duration-150 hover:-translate-y-0.5">
        <MessageSquarePlus className="h-4 w-4" />
        Leave a note
      </button>
    );
  }

  return (
    <div className="animate-slide-left fixed bottom-6 right-6 z-40 w-[min(92vw,26rem)] border border-stone-300 bg-white shadow-[6px_6px_0px_#0f172a]" data-testid="note-box">
      <div className="flex items-start justify-between border-b border-stone-200 bg-[#0f172a] px-5 py-4 text-white">
        <div>
          <p className="font-display text-lg font-bold tracking-tight">Leave a note</p>
          <p className="mt-1 font-mono-data text-[11px] uppercase tracking-wider opacity-70">wrong verdict · token issue · anything else</p>
        </div>
        <button onClick={() => setOpen(false)} data-testid="note-close-btn" className="p-1 opacity-70 transition-opacity hover:opacity-100">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="p-5">
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="note-message-input"
          placeholder="The verdict on my GST entry looked off, or my reviews ran out sooner than expected…"
          className="rounded-none border-stone-300 text-sm focus-visible:ring-stone-900" />
        <Input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="note-email-input"
          placeholder="your email (optional, only so we can reply)"
          className="mt-3 rounded-none border-stone-300 text-sm focus-visible:ring-stone-900" />
        <button onClick={send} disabled={sending} data-testid="note-send-btn"
          className="mt-4 flex w-full items-center justify-center gap-2 bg-[#0f172a] px-6 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800 disabled:opacity-60">
          {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <><Send className="h-4 w-4" /> Send note</>}
        </button>
      </div>
    </div>
  );
};
